import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import API from '../../api';

import { useRefreshToken } from '../../common/hooks/useRefreshToken';
import Loader from '../../components/Loader';
import notify from '../../components/Notification/helper';
import ViewPage from '../../layouts/ViewPage';
import LabelAndDisplay from '../../components/LabelAndDisplay';
import ImageContainer from '../../components/shared/ImageContainer';

import Wysiwyg from '../../components/shared/Wysiwyg';

import { getTranslation, languages } from '../../utils/translate';
import Nutrition from '../../components/Nutrition';
import { Chip, ListItemText, MenuItem, MenuList, Tab, Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import BeefOverride from '../../components/BeefOverride';
import useSWR from 'swr';
import ImageCard from '../../components/ImageCard';
import LazyLoad from 'react-lazyload';
import ThreeDViewer from '../../components/ThreeDViewer';
import VideosGallery from '../../components/shared/VideosGallery';
import BarcodeForm from '../../components/BarcodeForm';
import { getBeefProductOverride } from '../../utils/overrides';
import PromotionsTab from './PromotionsTab';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function BeefCutView() {
  useRefreshToken();

  const params = useParams();

  const [language, setLanguage] = useState(languages[0]);

  const retailer = useSelector(state => state.retailer);
  
  const beefCutId = params.beefCutId;
  const [beefCut, setBeefCut] = useState({});

  const [title, setTitle] = useState('');
  const [description] = useState('');
  
  const [fields, setFields] = useState([]);
  const [sidebarFields, setSidebarFields] = useState([]);
  const [visualFields, setVisualFields] = useState([]);

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0);

  const menuItems = [
    'Overview',
    'Override',
    'Visuals',
    'Add Barcode',
    'Barcodes',
    'Recipes',
    'Promotions',
  ];

  const { data: recipes } = useSWR({
    url: `recipe?meta=*`,
    fields: [
      "*",
      "layout_translations.*",
      "imagegallery.directus_files_id",
    ],
    filter: {
      _and: [
        {
          '_or': [
            {
              retailers: {
                retailer_id: {
                  '_eq': retailer.id
                }
              }
            },
            {
              retailer: {
                id: {
                  '_eq': retailer.id
                }
              }
            }
          ]
        },
        {
          beef_products: {
            beef_product_id: {
              '_eq': beefCutId
            }
          },
        }
      ],
    },
    sort: 'date_created',
    limit: -1,
  });

  const { data: barcodes } = useSWR({
    url: `barcode?meta=*`,
    fields: [
      "*",
      "beef_product_id.*",
      "beef_product_id.translations.*",
    ],
    filter: {
      _and: [
        {
          '_or': [
            {
              owner: {
                id: {
                  '_eq': retailer.id
                }
              }
            }
          ]
        },
        {
          beef_product_id: {
            '_eq': beefCutId
          }
        }
      ],
    },
    sort: '-date_created',
    limit: 30,
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const actionOptions = [
    {
      key: 'Add to Library',
      content: 'Add to Library ',
      onClick: async () => {
        await addRecipeToRecipeBook(beefCutId);
        makeGetQrCodeRequest();

      },
    },
    {
      key: 'Remove from Library',
      content: 'Remove from Library ',
      onClick: async () => {
        await removeRecipeFromRecipeBook(beefCutId);
        makeGetQrCodeRequest();
      },
    },
  ];

  useEffect(() => {
    if (!beefCutId) return;

    makeGetQrCodeRequest();
  }, [beefCutId, language]);

  const makeGetQrCodeRequest = async () => {
    try {
      // setLoading(true);

      const beefCutResponse = await API.getBeefProduct(beefCutId,
        {
          beef_product_overrides: {
            _filter: {
              retailer: {
                _eq: retailer.id
              }
            }
          },
        },
        {
          fields: '*,translations.*,primal_configurations.*,food_service_configurations.*,beef_product_overrides.*,beef_product_overrides.translations.*,Nutrients.*.*'
        }
      );

      console.log('beefCutResponse: ', beefCutResponse);

      const nutrition = beefCutResponse.Nutrients?.id ? await API.getNutrition(beefCutResponse.Nutrients.id) : {};

      const beefOverride = getBeefProductOverride(beefCutResponse, retailer, { code: language.code });
      const translatedBeefOverride = getTranslation(beefOverride, { key: 'languages_code', code: language.code });

      const translatedRecipe = getTranslation(beefCutResponse, { key: 'languages_id', code: language.code });
      const wholesaleSettings = getTranslation(beefCutResponse, { key: 'languages_code', code: language.code }, 'primal_configurations');
      const foodServiceSettings = getTranslation(beefCutResponse, { key: 'languages_code', code: language.code }, 'food_service_configurations');

      const beefCutName = translatedBeefOverride?.name ? `${translatedBeefOverride?.name} (${translatedRecipe?.name})` : translatedRecipe?.name;

      const beefCutImage = beefCutResponse.image ? `${process.env.REACT_APP_API_URL}/assets/${beefCutResponse.image}` : '';
      
      setTitle(beefCutName);
      setBeefCut(beefCutResponse);

      setFields([
        {
          key: 'Image',
          children: (
            <ImageContainer
              url={beefCutImage}
              alt={translatedRecipe.name}
              sx={{
                height: '20rem',
              }}
            />
          )
        },
        {
          key: 'Name',
          value: beefCutName,
        },
        {
          key: 'Description',
          children: (
            <Typography>
              <Wysiwyg>{translatedRecipe.description}</Wysiwyg>
            </Typography>
          )
        },
        {
          key: 'Specifications',
          children: (
            <Typography>
              <Wysiwyg>{translatedRecipe.specifications}</Wysiwyg>
            </Typography>
          )
        },
        ...(
          wholesaleSettings.key_information ?
          [
            {
              key: 'Key Information',
              children: (
                <Typography>
                  <Wysiwyg>{wholesaleSettings.key_information}</Wysiwyg>
                </Typography>
              )
            },
          ] : []
        ),
        ...(
          wholesaleSettings.purchasing_content ?
          [
            {
              key: 'Purchasing Content',
              children: (
                <Typography>
                  <Wysiwyg>{wholesaleSettings.purchasing_content}</Wysiwyg>
                </Typography>
              )
            },
          ] : []
        ),
        ...(
          wholesaleSettings.merchandising_content ?
          [
            {
              key: 'Merchandising Content',
              children: (
                <Typography>
                  <Wysiwyg>{wholesaleSettings.merchandising_content}</Wysiwyg>
                </Typography>
              )
            },
          ] : []
        )
      ]);

      setVisualFields([
        ...(
          (beefCutResponse?.threed_file || beefCutResponse?.threed_file_annotated) ? [
            {
              key: '3D',
              children: (
                <ThreeDViewer
                  plainModelFile={beefCutResponse.threed_file}
                  annotatedModelFile={beefCutResponse.threed_file_annotated}
                />
              )
            }
          ] : []
        ),
        ...(
          foodServiceSettings?.preparation_videos?.length > 0 ? [
            {
              key: 'Preparation Videos',
              children: (
                <VideosGallery videos={foodServiceSettings?.preparation_videos} />
              )
            },
          ] : []
        ),
        ...(
          foodServiceSettings?.merchandising_videos?.length > 0 ? [
            {
              key: 'Merchandising Videos',
              children: (
                <VideosGallery videos={foodServiceSettings?.merchandising_videos} />
              )
            },
          ] : []
        ),
        ...(
          foodServiceSettings?.culinary_videos?.length > 0 ? [
            {
              key: 'Culinary Videos',
              children: (
                <VideosGallery videos={foodServiceSettings?.culinary_videos} />
              )
            },
          ] : []
        ),
        ...(
          foodServiceSettings?.purchasing_videos?.length > 0 ? [
            {
              key: 'Purchasing Videos',
              children: (
                <VideosGallery videos={foodServiceSettings?.purchasing_videos} />
              )
            },
          ] : []
        ),
      ]);

      setSidebarFields([
        ...(
          nutrition.id ? 
          [
              {
                key: 'Nutrition',
                children: (
                  <Nutrition nutrition={nutrition} recipe={beefCutResponse} />
                )
              },
          ] : []
        )
      ])
    } catch (error) {
      console.log('error: ', error);
      notify('error', 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  const makeRetailerPatchRequest = async (payload) => {
    return await API.updateRetailer(payload);
  }

  const addRecipeToRecipeBook = async (beefCutId) => {
    try {
      setLoading(true);

      // get list of recipes
      const payload = {
        recipes: retailer.recipes
      }

      // append
      payload.recipes.push({
        recipe_id: beefCutId
      });

      await makeRetailerPatchRequest(payload);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setLoading(false);
    }
  }

  const removeRecipeFromRecipeBook = async (beefCutId) => {
    try {
      setLoading(true);

      // get list of recipes
      const payload = {
        recipes: retailer.recipes.filter(recipe => recipe.recipe_id !== beefCutId)
      }

      await makeRetailerPatchRequest(payload);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <ViewPage
        title={ title }
        description={ description }
        actionOptions={ actionOptions }
        previewUrl={`https://${retailer.subdomain}/recipe-list?beef_cut=${beefCutId}`}
        addToLibrary={{
          junctionTable: 'beef_product_retailer',
          filter: {
            _and: [
              { beef_product_id: { _eq: beefCutId } },
              { retailer_id: { _eq: retailer.id } }
            ]
          },
          addPayload: {
            beef_product_id: beefCutId,
            retailer_id: retailer.id
          },
          removePayload: {
            query: {
              filter: {
                retailer_id: {
                  _eq: retailer.id,
                },
                beef_product_id: {
                  _eq: beefCutId,
                }
              }
            }
          },
        }}
        breadcrumbsLinks={[
          {
            url: '/recipes-menu',
            content: 'Content Library',
          },
          {
            url: `/beef-cuts`,
            content: 'Beef Cuts',
          }
        ]}
        language={language}
        setLanguage={setLanguage}
        prefix='View in '
      >
        <Box
          sx={{
            flexGrow: 1,
            my: 3,
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              width: '100%',
              display: {
                xs: 'none',
                lg: 'block',
              }
            }}
          >
            <Tabs
              value={value}
              // variant="scrollable"
              // scrollButtons={false}
              onChange={handleChange}
              sx={{
                // width: '100%',
              }}
            >
              <Tab label="Overview" {...a11yProps(0)} />
              <Tab label="Override" {...a11yProps(1)} />
              <Tab label="Visuals" {...a11yProps(2)} />
              <Tab label="Add Barcode" {...a11yProps(3)} />
              <Tab label={`Barcodes (${barcodes?.meta.filter_count || 0})`} {...a11yProps(4)} />
              <Tab label={`Recipes (${recipes?.meta.filter_count || 0})`} {...a11yProps(5)} />
              <Tab label="Promotions" {...a11yProps(6)} />
            </Tabs>
          </Box>

          <Box>
            <MenuList
              sx={{
                width: '100%',
                borderLeft: '1px solid #e0e0e0',
                borderRight: '1px solid #e0e0e0',
                p: 0,
                display: {
                  xs: 'block',
                  lg: 'none',
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Fragment key={item}>
                  <MenuItem
                    sx={{
                      background: '#efeeee',
                      my: 0.5
                    }}
                    selected={index === value}
                    onClick={(event) => handleChange(event, index)}
                  >
                    <ListItemText
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                      }}
                    >
                      {item}
                    </ListItemText>
                  </MenuItem>
                </Fragment>
              ))}
            </MenuList>
          </Box>

          <Box>
            <CustomTabPanel value={value} index={0}>
              <Typography
                className='form-section-title'
                variant="h4"
              >
                Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {
                          fields.map(field => (
                            <LabelAndDisplay
                              key={field.key}
                              label={field.key}
                              value={field.value}
                            >
                              {field.children}
                            </LabelAndDisplay>
                          ))
                        }
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Box sx={{ flexGrow: 1, my: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {
                          sidebarFields.map(field => (
                            <LabelAndDisplay
                              direction='column'
                              key={field.key}
                              label={field.key}
                              value={field.value}
                            >
                              {field.children}
                            </LabelAndDisplay>
                          ))
                        }
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <BeefOverride
                beefCut={beefCut}
                onComplete={makeGetQrCodeRequest}
              />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={2}>
              <Typography
                className='form-section-title'
                variant="h4"
              >
                Visuals
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {
                          visualFields.map(field => (
                            <LabelAndDisplay
                              key={field.key}
                              label={field.key}
                              value={field.value}
                            >
                              {field.children}
                            </LabelAndDisplay>
                          ))
                        }
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Box sx={{ flexGrow: 1, my: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={3}>
              <Box sx={{ flexGrow: 1 }}>
                <BarcodeForm
                  beefCut={beefCut}
                />
              </Box>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={4}>
              <Typography
                className='form-section-title'
                variant="h4"
              >
                Barcodes
              </Typography>
              <Box sx={{ flexGrow: 1, my: 3 }}>
                <Grid container spacing={2}>
                  {barcodes?.data?.map((barcode) => (
                    <Grid
                      key={barcode.id}
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      lg={3}
                      xl={2}
                    >
                      <Box
                        sx={{
                          height: '100%',
                        }}
                      >
                        <LazyLoad
                          height='100%'
                        >
                          <ImageCard
                            url={`/barcodes/${barcode.id}`}
                            item={{
                              title: barcode.code,
                              description: barcode.date_updated ? `Last updated ${new Date(barcode.date_updated).toLocaleDateString()}` : 'Created on ' + new Date(barcode.date_created).toLocaleDateString(),
                              url: `${process.env.REACT_APP_API_URL}/assets/${barcode?.beef_product_id?.image}`,
                            }}
                            footerAction={(
                              <Chip
                                label={barcode?.status}
                                color={barcode?.status?.toLowerCase() === 'published' ? 'success' : 'warning'}
                                variant="outlined"
                                sx={{
                                  textTransform: 'capitalize',
                                  width: '100%',
                                }}
                              />
                            )}
                            imageBackground='#fff'
                          />
                        </LazyLoad>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={5}>
              <Typography
                className='form-section-title'
                variant="h4"
              >
                Recipes
              </Typography>
              <Box sx={{ flexGrow: 1, my: 3 }}>
                <Grid container spacing={2}>
                  {recipes?.data?.map((recipe) => (
                    <Grid
                      key={recipe.id}
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      lg={3}
                      xl={2}
                    >
                      <Box
                        sx={{
                          height: '100%',
                        }}
                      >
                        <LazyLoad
                          height='100%'
                        >
                          <ImageCard
                            description={getTranslation(recipe, { key: 'languages_code', code: language.code }, 'layout_translations').summary}
                            url={`/recipes/${recipe.id}`}
                            item={{
                              title: getTranslation(recipe, { key: 'languages_code', code: language.code }, 'layout_translations').name,
                              description: getTranslation(recipe, { key: 'languages_code', code: language.code }, 'layout_translations').summary,
                              url: `${process.env.REACT_APP_API_URL}/assets/${recipe?.imagegallery[0]?.directus_files_id}` || ''
                            }}
                            footerAction={<Box sx={{ my: 0.5 }} />}
                          />
                        </LazyLoad>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={6}>
              <PromotionsTab beefCut={beefCut} />
            </CustomTabPanel>
          </Box>
        </Box>
      </ViewPage>
    </>
  )
}

export default BeefCutView